import { useState, useEffect } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import { getCashflowForecast } from "../../../services/tenantUserAdminService";
import { TC } from "../../../tokens/text";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from "recharts";

export default function CashflowForecastWidget({ tenantId }) {
  const [loading, setLoading] = useState(true);
  const [forecast, setForecast] = useState([]);

  useEffect(() => {
    const demoMode = typeof window !== 'undefined' && (window.__LEDGERHUB_DEMO || new URLSearchParams(window.location.search).get('demo') === 'true');
    if (demoMode) {
      const now = new Date();
      const points = [];
      let cum = 5000;
      for (let i = 0; i < 90; i++) {
        const d = new Date(now); d.setDate(now.getDate() + i);
        cum += Math.round(Math.sin(i/10) * 200 + 150);
        points.push({ date: d.toISOString().slice(0,10), net: cum });
      }
      setForecast(points);
      setLoading(false);
      return;
    }
    if (!tenantId) return;
    setLoading(true);
    getCashflowForecast(tenantId)
      .then((res) => setForecast((res.points || []).map(p => ({ date: p.date, net: Number(p.net || 0) })) ))
      .catch(() => setForecast([]))
      .finally(() => setLoading(false));
  }, [tenantId]);

  if (loading) return <VuiBox display="flex" justifyContent="center" alignItems="center" height="100%"><CircularProgress size={24} /></VuiBox>;
  if (!forecast.length) return <VuiBox p={2}><VuiTypography color="text">No forecast data available.</VuiTypography></VuiBox>;

  const total30 = forecast.slice(0, 30).reduce((s, p) => s + p.net, 0);
  const total90 = forecast.slice(0, 90).reduce((s, p) => s + p.net, 0);

  return (
    <VuiBox p={1} display="flex" flexDirection="column" gap={1} height="100%">
      <VuiBox display="flex" justifyContent="space-between" alignItems="center">
        <VuiTypography sx={{ color: TC.secondary }}>30‑day projection</VuiTypography>
        <VuiTypography sx={{ color: TC.primary, fontWeight: 700 }}>£{total30.toLocaleString()}</VuiTypography>
      </VuiBox>
      <VuiBox sx={{ flex: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height={140}>
          <AreaChart data={forecast.slice(0, 90)} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="gradNet" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#2196f3" stopOpacity={0.6} />
                <stop offset="100%" stopColor="#2196f3" stopOpacity={0.08} />
              </linearGradient>
            </defs>
            <XAxis dataKey="date" tickFormatter={(d)=>d.slice(5)} hide />
            <YAxis tickFormatter={(v)=>`£${Math.round(v)}`} width={60} />
            <Tooltip formatter={(v)=>`£${v}`} labelFormatter={(l)=>l} />
            <Area type="monotone" dataKey="net" stroke="#2196f3" fill="url(#gradNet)" />
          </AreaChart>
        </ResponsiveContainer>
      </VuiBox>
      <VuiBox display="flex" justifyContent="space-between">
        <VuiTypography sx={{ color: TC.secondary }}>90‑day projection</VuiTypography>
        <VuiTypography sx={{ color: TC.primary, fontWeight: 700 }}>£{total90.toLocaleString()}</VuiTypography>
      </VuiBox>
    </VuiBox>
  );
}

